import React from 'react';
import { GetServerSideProps } from 'next';
import CategoryApi from '@lambda/apis/src/category';
import axios from 'axios';
import {
  CategoryTree,
  ProductCategory,
  CategoryLandingPageKey,
} from '@lambda/apis/src/category/types';
import { uniq } from 'lodash-es';
import settings from '@/settings';
import categories from '@/settings/categories';
import {
  CategoryLandingPageProps,
  StreamlineCategoryTree,
  SubCategoryDataT,
} from '@/components/category/types';
import { REDIRECT_HOME } from '@/lib/common';
import CategoryLandingPage from '@/components/category/CategoryLandingPage';
import {
  findCategoryByPk,
  getChildCategoryNames,
  notNull,
} from '@/components/category/helpers';
import { fetchOffers } from '@/api/collections/category/category-landing-search';

type Path = { handle: string };

/**
 * Generates the category tree with matching subcategories and their offers
 * @param categoryTree - The main category page of type category tree
 * @param categoryItem - The main category page
 * @returns The updated category tree with matching subcategories and their offers
 */
async function streamlineCategoryTree(
  categoryTree: CategoryTree,
  categoryItem: ProductCategory,
): Promise<StreamlineCategoryTree> {
  // Fetch offers for matching subcategories
  const subCategories = (
    await Promise.all(
      categoryItem.subCategories.map(async (subCategory) => {
        const foundCategory = findCategoryByPk(categoryTree, subCategory.pk);

        if (foundCategory && foundCategory.isActive === 1) {
          // Get both direct and subchild category names
          const categoryNames = getChildCategoryNames(foundCategory);

          const data: SubCategoryDataT = {
            ...subCategory,
            pk: foundCategory.pk,
            name: foundCategory.name,
            offerCount: foundCategory.offerCount?.[settings.store] ?? 0,
            categories: categoryNames,
            config: subCategory.config,
          };

          if (subCategory.storeConfig)
            data.storeConfig = subCategory.storeConfig;

          return data;
        }

        return null;
      }),
    )
  )
    .filter(notNull)
    .sort((a, b) => (b.offerCount ?? 0) - (a.offerCount ?? 0));

  // Fetch offers for the main category
  const excludedCategories = ['accessories', 'accessory'];

  const categoryNames = uniq(
    getChildCategoryNames(categoryTree).filter(
      (c) =>
        !excludedCategories.some((excludedCategroy) =>
          c.toLowerCase().includes(excludedCategroy),
        ),
    ),
  );

  const results = await fetchOffers(categoryNames, 20);

  const data: StreamlineCategoryTree = {
    config: categoryItem.config,
    name: categoryTree.name,
    offers: results.offers,
    options: results.options,
    pk: categoryTree.pk,
    subCategories,
  };

  if (categoryTree?.warrantyInfo?.[settings.store])
    data.categoryWarrantyInfo = categoryTree?.warrantyInfo?.[settings.store];

  if (categoryItem.storeConfig) data.storeConfig = categoryItem.storeConfig;

  return data;
}

const getCategoryLandingPageProps: GetServerSideProps<
  CategoryLandingPageProps,
  Path
> = async (event) => {
  const { query } = event;
  const handle = (query.handle as string)?.toLowerCase();

  // Redirect to home if no valid handle is provided
  if (!handle) return REDIRECT_HOME;

  const categoryLandingPage = categories[handle as CategoryLandingPageKey];

  // Redirect to home if the categoryLandingPage does not exist
  if (!categoryLandingPage) return REDIRECT_HOME;

  const categoryApi = new CategoryApi();

  // Fetch category tree data from API
  const categoryTree =
    process.env.STAGE === 'prod'
      ? await categoryApi.fetchCategoryByPk(
          categoryLandingPage.pk,
          {
            includes: ['children', 'warrantyInfo'],
          },
          settings.store,
        )
      : await axios
          .get(
            `https://reebelo.com.au/api/categories/${categoryLandingPage.pk}?includes[]=children&includes[]=warrantyInfo&store=${settings.store}`,
          )
          .then((res) => res.data);

  // Redirect to home if the categoryTree does not exist
  if (!categoryTree) return REDIRECT_HOME;

  const category = await streamlineCategoryTree(
    categoryTree,
    categoryLandingPage,
  );

  if (!category) return REDIRECT_HOME;

  // Redirect if the category has no offers
  if (category.offers.length === 0) return REDIRECT_HOME;

  const props: CategoryLandingPageProps = {
    ...category,
    handle,
  };

  return { props };
};

// Server-side function to fetch the collection props
export const getServerSideProps: GetServerSideProps<any, Path> = async (
  event,
) => {
  const response = await getCategoryLandingPageProps(event);

  return response;
};

// Main component rendering the category landing page
export default function CategoryLanding(props: CategoryLandingPageProps) {
  return <CategoryLandingPage {...props} />;
}
